


















































import { DateWithoutHour } from '@/models/date-time/date-without-hour'
import { DeliveryConfigState, DeliveryMethod, DeliveryMethodDetail, DeliveryTime, OkihaiPlace } from '@/models/delivery'
import SelectDeliveryDate from '@spa/components/pages/order/edit/delivery/SelectDeliveryDate.vue'
import SelectDeliveryMethod from '@spa/components/pages/order/edit/delivery/SelectDeliveryMethod.vue'
import SelectDeliveryTime, {
  DeliveryTimeOption
} from '@spa/components/pages/order/edit/delivery/SelectDeliveryTime.vue'
import { PluginMixin } from '@spa/mixins/plugin'
import { Mixins } from 'vue-mixin-decorator'
import { Component, Emit, Prop } from 'vue-property-decorator'

@Component({
  components: {
    SelectDeliveryDate,
    SelectDeliveryMethod,
    SelectDeliveryTime,
  },
})
export default class SetDeliveryConfig extends Mixins<PluginMixin>(
  PluginMixin
) {
  @Prop({ required: true })
  deliveryConfig: DeliveryConfigState

  /**
   * - Engine が持つ appSettings の設定値によって、日時指定の可否が切り替わる
   * - 予約商品の場合、日時指定はできない
   *
   * 上記のように、配送方法の外部要因に依って日時指定の可否が切り替わることがある.
   * 最終的にはWeb APIに集約させて、クライアントは配送日時指定の有無だけで判断可能にしたい.
   * Web APIに集約した場合、この Prop は削除可能となる.
   *
   * 現時点(at 2021/02/19)では、実装をすすめることを優先して、既存の仕組みに乗ることにした.
   */
  @Prop({ required: true })
  canSelectDeliveryPeriod: boolean

  get selectedDeliveryDate(): DateWithoutHour | null {
    return this.deliveryConfig.selectedDeliveryDate
  }

  get initialDeliveryMethod(): DeliveryMethodDetail {
    return this.deliveryConfig.selectedDeliveryMethod
  }

  get deliverableDates(): DateWithoutHour[] {
    return this.deliveryConfig.selectedDeliveryMethod.deliveryDateTimes.map(
      dt => dt.date
    )
  }

  get deliveryDate(): string | null {
    const selectedDeliveryDate = this.deliveryConfig.selectedDeliveryDate

    return selectedDeliveryDate ? selectedDeliveryDate.formattedDate('YYYY/M/DD') : null
  }

  get deliveryTimeSlot(): string {
    return this.deliveryConfig.selectedDeliveryTime.id
  }

  get deliveryTimeOptionList(): DeliveryTimeOption[] {
    if (!this.selectedDeliveryDate) {
      return []
    }

    const deliveryDateTime = this.deliveryConfig.selectedDeliveryMethod.deliveryDateTimes.find(
      dt => dt.date.isEqualTo(this.selectedDeliveryDate!)
    )

    return deliveryDateTime
      ? deliveryDateTime.timeList.map(dt => ({ code: dt.id, label: dt.label }))
      : []
  }

  get deliveryDateIsSelectable(): boolean {
    if (!this.canSelectDeliveryPeriod) {
      return false
    }

    return this.deliverableDates.length > 0
  }

  get deliveryTimeIsSelectable(): boolean {
    if (
      ! (
        this.canSelectDeliveryPeriod &&
        this.deliveryDateIsSelectable &&
        this.selectedDeliveryDate
      )
    ) {
      return false
    }

    /**
     * NOTE: 配送日ごとに、選択可能な配送時間帯が異なる可能性があるため、
     * サーバーレスポンスデータから、配送日ごとに配送時間帯の指定可否及び指定可能な範囲を取得可能.
     * しかし現在の仕様では全ての日において、時間帯指定の可否及び範囲は同じ
     * 従って、ここでは配送可能日の初日データだけを見て時間帯指定の可否を決める
     */
    const selectedDateTime = this.deliveryConfig.selectedDeliveryMethod.deliveryDateTimes.find(
      dt => dt.date.isEqualTo(this.selectedDeliveryDate!)
    )

    return selectedDateTime !== undefined && selectedDateTime.timeList.length > 1
  }

  get okihaiPlaceIsSelectable(): boolean {
    return this.deliveryConfig.selectedDeliveryMethod.okihaiPlaces.length > 1
  }
  get okihaiPlaces() {
    return this.deliveryConfig.selectedDeliveryMethod.okihaiPlaces.map(op => ({
      code: op.id,
      label: op.label,
    }))
  }
  get selectedOkihaiPlace() {
    return (this.deliveryConfig.selectedOkihaiPlace || { id: null }).id
  }

  onChangeDeliveryMethod(dm: DeliveryMethod) {
    this.changeDeliveryMethod(this.deliveryConfig, dm)
  }

  onChangeDeliveryDate(d: DateWithoutHour) {
    this.changeDeliveryConfig(this.deliveryConfig.changeDeliveryDate(d))
  }

  onChangeDeliveryTime(deliveryTime: DeliveryTime) {
    this.changeDeliveryConfig(this.deliveryConfig.changeDeliveryTime(deliveryTime))
  }

  onChangeOkihaiPlace(okihaiPlace: OkihaiPlace) {
    this.changeDeliveryConfig(this.deliveryConfig.changeOkihaiPlace(okihaiPlace))
  }

  @Emit('change-delivery-method')
  changeDeliveryMethod(_c: DeliveryConfigState, _dm: DeliveryMethod) {
    // do nothing
  }

  @Emit('change-delivery-config')
  changeDeliveryConfig(_c: DeliveryConfigState) {
    // do nothing
  }
}
